import type {
  AssessmentAssignmentDetail,
  AssessmentAssignmentInput,
  AssessmentAssignmentView,
  AssignableAssessment,
  MyAssessmentAssignment,
  DocumentRequestDetail,
  DocumentRequestInput,
  DocumentRequestView,
  DocumentSubmissionView,
  MyDocumentRequest,
  RecruiterContactInput,
  RecruiterContactView,
  ResumeReviewDetail,
  ResumeReviewSummary,
  ResumeReviewVerdict,
} from '@welynk/types';
import { del, get, patch, post, put } from './http';
import { API_ENDPOINTS } from '../api-endpoints';
import { getApiClient } from '../api-client-factory';
import { downloadBlob } from '../download';

/** College workspace tools: recruiter contacts, document collection, resume reviews. */
export class CollegeWorkspaceApiService {
  static recruiterContacts(orgId: string, company?: string) {
    return get<RecruiterContactView[]>(API_ENDPOINTS.RECORDS.RECRUITER_CONTACTS(orgId), {
      company,
    });
  }
  static createRecruiterContact(orgId: string, input: RecruiterContactInput) {
    return post<RecruiterContactView>(API_ENDPOINTS.RECORDS.RECRUITER_CONTACTS(orgId), input);
  }
  static updateRecruiterContact(orgId: string, id: string, input: RecruiterContactInput) {
    return put<RecruiterContactView>(API_ENDPOINTS.RECORDS.RECRUITER_CONTACT(orgId, id), input);
  }
  static deleteRecruiterContact(orgId: string, id: string) {
    return del<{ deleted: boolean }>(API_ENDPOINTS.RECORDS.RECRUITER_CONTACT(orgId, id));
  }

  static documentRequests(orgId: string) {
    return get<DocumentRequestView[]>(API_ENDPOINTS.RECORDS.DOCUMENT_REQUESTS(orgId));
  }
  static documentRequest(orgId: string, id: string) {
    return get<DocumentRequestDetail>(API_ENDPOINTS.RECORDS.DOCUMENT_REQUEST(orgId, id));
  }
  static createDocumentRequest(orgId: string, input: DocumentRequestInput) {
    return post<DocumentRequestView>(API_ENDPOINTS.RECORDS.DOCUMENT_REQUESTS(orgId), input);
  }
  static updateDocumentRequest(
    orgId: string,
    id: string,
    input: {
      title?: string;
      description?: string | null;
      dueAt?: string | null;
      status?: 'OPEN' | 'CLOSED';
    }
  ) {
    return patch<DocumentRequestView>(API_ENDPOINTS.RECORDS.DOCUMENT_REQUEST(orgId, id), input);
  }
  static reviewDocumentSubmission(
    orgId: string,
    submissionId: string,
    decision: 'APPROVE' | 'REJECT',
    note?: string
  ) {
    return post<DocumentSubmissionView>(
      API_ENDPOINTS.RECORDS.DOCUMENT_SUBMISSION_REVIEW(orgId, submissionId),
      { decision, note }
    );
  }
  static async downloadDocumentSubmission(orgId: string, submissionId: string, fileName: string) {
    const res = await getApiClient().get(
      API_ENDPOINTS.RECORDS.DOCUMENT_SUBMISSION_FILE(orgId, submissionId),
      { responseType: 'blob' }
    );
    downloadBlob(res.data as Blob, fileName);
  }
  static myDocumentRequests() {
    return get<MyDocumentRequest[]>(API_ENDPOINTS.RECORDS.MY_DOCUMENT_REQUESTS);
  }
  static submitDocument(requestId: string, documentId: string) {
    return put<DocumentSubmissionView>(API_ENDPOINTS.RECORDS.MY_DOCUMENT_SUBMISSION(requestId), {
      documentId,
    });
  }

  static reviewQueue(orgId: string, view: 'OPEN' | 'COMPLETED') {
    return get<ResumeReviewSummary[]>(API_ENDPOINTS.RECORDS.RESUME_REVIEWS(orgId), { view });
  }
  static review(orgId: string, id: string) {
    return get<ResumeReviewDetail>(API_ENDPOINTS.RECORDS.RESUME_REVIEW(orgId, id));
  }
  static startReview(orgId: string, id: string) {
    return post<ResumeReviewDetail>(`${API_ENDPOINTS.RECORDS.RESUME_REVIEW(orgId, id)}/start`);
  }
  static commentOnReview(
    orgId: string,
    id: string,
    input: { section?: string | null; body: string }
  ) {
    return post<ResumeReviewDetail>(
      `${API_ENDPOINTS.RECORDS.RESUME_REVIEW(orgId, id)}/comments`,
      input
    );
  }
  static completeReview(
    orgId: string,
    id: string,
    input: { verdict: ResumeReviewVerdict; summary: string }
  ) {
    return post<ResumeReviewDetail>(
      `${API_ENDPOINTS.RECORDS.RESUME_REVIEW(orgId, id)}/complete`,
      input
    );
  }
  static myResumeReviews() {
    return get<ResumeReviewDetail[]>(API_ENDPOINTS.RECORDS.MY_RESUME_REVIEWS);
  }
  static requestResumeReview(input: { organizationId: string; resumeId: string; note?: string }) {
    return post<ResumeReviewSummary>(API_ENDPOINTS.RECORDS.MY_RESUME_REVIEWS, input);
  }
  static cancelResumeReview(id: string) {
    return post<{ cancelled: boolean }>(API_ENDPOINTS.RECORDS.MY_RESUME_REVIEW_CANCEL(id));
  }

  static assignableAssessments(orgId: string) {
    return get<AssignableAssessment[]>(API_ENDPOINTS.RECORDS.ASSIGNABLE_ASSESSMENTS(orgId));
  }
  static assessmentAssignments(orgId: string) {
    return get<AssessmentAssignmentView[]>(API_ENDPOINTS.RECORDS.ASSESSMENT_ASSIGNMENTS(orgId));
  }
  static assessmentAssignment(orgId: string, id: string) {
    return get<AssessmentAssignmentDetail>(API_ENDPOINTS.RECORDS.ASSESSMENT_ASSIGNMENT(orgId, id));
  }
  static assignAssessment(orgId: string, input: AssessmentAssignmentInput) {
    return post<AssessmentAssignmentView>(
      API_ENDPOINTS.RECORDS.ASSESSMENT_ASSIGNMENTS(orgId),
      input
    );
  }
  static removeAssessmentAssignment(orgId: string, id: string) {
    return del<{ archived: boolean }>(API_ENDPOINTS.RECORDS.ASSESSMENT_ASSIGNMENT(orgId, id));
  }
  static myAssessmentAssignments() {
    return get<MyAssessmentAssignment[]>(API_ENDPOINTS.RECORDS.MY_ASSESSMENT_ASSIGNMENTS);
  }
}
